import {
    init as initSDK,
    initData,
    locationManager,
    miniApp,
    retrieveLaunchParams,
    setDebug,
    swipeBehavior,
    themeParams,
    viewport,
} from '@tma.js/sdk';
import { backButton } from './helpers/backButton';
import { isMockedEnvironment, mockSafeAreaCssVars } from './helpers/telegram';

const initDebugTools = (debug: boolean): void => {
    setDebug(debug);
    if (debug) {
        import('eruda').then(lib => lib.default.init()).catch(console.error);
    }
};

const checkRequiredComponents = (): void => {
    if (isMockedEnvironment()) {
        console.warn('⚠️ Skipping component support check in mocked environment');
        return;
    }

    const requiredComponents = [
        { name: 'Back Button', supported: backButton.isSupported() },
        { name: 'Mini App', supported: miniApp.isSupported() },
    ];

    const unsupportedComponents = requiredComponents.filter(component => !component.supported).map(component => component.name);

    if (unsupportedComponents.length > 0) {
        throw new Error(`Unsupported components: ${unsupportedComponents.join(', ')}`);
    }
};

const mountUIComponents = (): void => {
    backButton.mount();
    miniApp.mount();
    themeParams.mount();
    initData.restore();
    swipeBehavior.mount();
};

const setupLocationManager = async (): Promise<void> => {
    try {
        if (locationManager.isSupported()) {
            await locationManager.mount();
        }
    } catch (error) {
        console.error('Location manager setup failed:', error);
    }
};

const setupViewport = async (): Promise<void> => {
    try {
        await viewport.mount();
        viewport.bindCssVars();

        if (isMockedEnvironment()) {
            mockSafeAreaCssVars();
        }

        if (!viewport.isExpanded()) {
            viewport.expand.ifAvailable();
        }

        const { tgWebAppPlatform } = retrieveLaunchParams();
        if (tgWebAppPlatform !== 'tdesktop') {
            viewport.requestFullscreen.ifAvailable();
        }
    } catch (error) {
        console.error('Viewport setup failed:', error);
    }
};

const initializeStyles = (): void => {
    miniApp.bindCssVars();
    themeParams.bindCssVars();
};

const disableVerticalSwipe = (): void => {
    swipeBehavior.disableVertical.ifAvailable();
};

export function init(debug: boolean): void {
    initDebugTools(debug);
    initSDK();
    checkRequiredComponents();
    mountUIComponents();
    void setupLocationManager();
    void setupViewport();
    initializeStyles();
    disableVerticalSwipe();
}
